import './IbanValidation.scss';

import React, { useState, useEffect } from 'react';

import { Form } from 'react-redux-form';
import PropTypes from 'prop-types';
import { FormattedText, formatText } from 'core/i18n';
import { recordEvent, PAGE_PRODUCT, recordIdentityEvent, recordEventCustomOnly } from 'utilities/uTagging';
import { checkConditionAndExecuteCallback } from 'utilities/toggling';
import { getUserProfileData } from 'utilities/userProfile';
import { OKIban } from 'ok-iban/dist/components/OKIban/OKIban';
import MessageBox from 'components/commons/MessageBox/MessageBox';
import ConditionalToggling from 'utilities/toggling/ConditionalToggling/ConditionalToggling';
import IbanTermsClause from './IbanTermsClause';
import FeatureTogglingRender from 'components/featureToggling/featureTogglingRender';

import { CREDIT_CARD, ROBO, TRADITIONAL } from 'constants/userProfile';
import { PATH_IDENTITY_IBAN, PATH_IDENTITY_IBAN_EVENT, PATH_IDENTITY } from 'constants/uTagConstants/paths';
import { EVENT_ONBOARDING } from 'constants/uTagConstants/RAConstants';
import { ONLINE_IBAN } from 'constants/featureFlagging';
import { COUNTRY_CODE_ES } from 'commonsConstants/countryCodes';
import { BANKIA_CODE, OPENBANK_CODE } from 'constants/iban';
import { isMobile } from 'utilities/browser';
import { ORIGIN_BY_CHANNEL } from 'constants/uTagConstants/origin';

const INVALID_IBAN_FORMAT = 'error input incorrecto / formato';
const VALID_IBAN_FORMAT = 'input correcto / formato';
const INVALID_IBAN = 'error input incorrecto / validacion por entidad bancaria "';
const VALID_IBAN = 'input correcto';

const IbanValidation = ({
    immStepsState,
    immIban,
    formActions,
    userProfile,
    userProfileForm,
    ibanActions: { ibanValidationActionCall },
    preLoaderActions: { showPreLoader, hidePreLoader },
    OKIbanActions: { saveIban },
    handleShowIbanErrorModal,
}) => {
    const [ibanInsert, setIbanInsert] = useState({ ibanInserted: '', isIbanAlreadyInserted: false });
    const [ibanValid, setIbanValid] = useState({ ibanErrorType: '', isIbanValid: false });
    const [showIbanDisclaimer, setShowIbanDisclaimer] = useState(false);
    const [creditCardValidationSuccessAndRecorded, setCreditCardValidationSuccessAndRecorded] = useState(false);
    const ibanIdentity = true;

    useEffect(() => {
        const { encryptedDocNumber, recordId, userId, investmentType } = getUserProfileData({ userProfile });
        recordEvent(
            PATH_IDENTITY_IBAN,
            PAGE_PRODUCT,
            PATH_IDENTITY_IBAN_EVENT,
            'iban',
            recordId,
            userId,
            '',
            investmentType,
            encryptedDocNumber,
            {
                categoriaEvento: EVENT_ONBOARDING,
                accionEvento: PATH_IDENTITY_IBAN_EVENT,
                etiquetaEvento: 'iban',
                onboardingOrigin: ORIGIN_BY_CHANNEL[userProfile.channel],
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const recordIbanValidationEvent = () => {
        const { isIbanValid } = ibanValid;
        checkConditionAndExecuteCallback(isIbanValid && !creditCardValidationSuccessAndRecorded, () =>
            recordIdentityEvent(EVENT_ONBOARDING, PATH_IDENTITY_IBAN_EVENT, VALID_IBAN_FORMAT)
        );
    };

    const storeInsertedIban = iban => {
        recordIbanValidationEvent();
        setIbanInsert({
            ibanInserted: iban,
            isIbanAlreadyInserted: true,
        });
    };

    useEffect(() => {
        const { isIbanAlreadyInserted, ibanInserted } = ibanInsert;

        const iban = immIban.get('iban');
        const ibanInStoreAndIbanInsertedAreEqual = ibanInserted === iban;

        if (isIbanAlreadyInserted && ibanInStoreAndIbanInsertedAreEqual) return;

        const ibanHasNoErrors = immIban.get('error') === '';
        checkConditionAndExecuteCallback(ibanHasNoErrors && !ibanInStoreAndIbanInsertedAreEqual, () =>
            storeInsertedIban(iban)
        );
    });

    const recordIdentityIbanTagging = ({ eventTag, customProcessEventTag }) => {
        const { encryptedDocNumber, recordId, userId, investmentType } = getUserProfileData({ userProfile });
        recordEvent(
            PATH_IDENTITY_IBAN,
            PAGE_PRODUCT,
            'validacion por iban',
            eventTag,
            recordId,
            userId,
            '',
            investmentType,
            encryptedDocNumber,
            {
                categoriaEvento: EVENT_ONBOARDING,
                accionEvento: PATH_IDENTITY_IBAN_EVENT,
                etiquetaEvento: customProcessEventTag,
            }
        );
    };

    const onHandleValidationError = value => {
        checkConditionAndExecuteCallback(value === 'validation-ibanFormatError', () => {
            recordIdentityIbanTagging({
                eventTag: `validacion por iban incorrecto - ${formatText(value)}`,
                customProcessEventTag: INVALID_IBAN_FORMAT,
            });
        });

        checkConditionAndExecuteCallback(value === 'validation-ibanEntityError', () => {
            recordIdentityIbanTagging({
                eventTag: `validacion por iban incorrecto / entidad${formatText(value)}`,
                customProcessEventTag: INVALID_IBAN,
            });
        });
    };

    const recordValidOrInvalidIban = (valid, type = '', updateValue = '') => {
        const { accessToken } = userProfile;
        setIbanValid({ isIbanValid: valid, ibanErrorType: type });
        setCreditCardValidationSuccessAndRecorded(false);
        setShowIbanDisclaimer(true);
        if (!valid || !updateValue) {
            recordEventCustomOnly(
                {
                    nombrePagina: PATH_IDENTITY,
                    categoriaEvento: 'eventos onboarding',
                    accionEvento: 'identidad/iban/',
                    etiquetaEvento: 'error input incorrecto / no corresponde con banco español',
                },
                PATH_IDENTITY
            );

            return;
        }

        showPreLoader();
        setTimeout(() => {
            ibanValidationActionCall(accessToken, updateValue).then(data => {
                if (data.status === 'OK') {
                    (async () => {
                        const response = await ibanValidationActionCall(accessToken, updateValue, CREDIT_CARD);
                        if (response.status === 'OK') {
                            saveIban(updateValue);
                            recordIdentityEvent(EVENT_ONBOARDING, PATH_IDENTITY_IBAN_EVENT, VALID_IBAN);
                            setCreditCardValidationSuccessAndRecorded(true);
                        } else {
                            handleShowIbanErrorModal(response.bank);
                        }
                    })();
                } else {
                    handleShowIbanErrorModal(data.bank);
                }
                hidePreLoader();
            });
        }, 1500);
    };

    const currentStep = immStepsState?.get('currentStep');
    const stepDetails = immStepsState?.get('steps');
    const step = stepDetails?.get(currentStep);
    const stepFinished = step?.get('finished');
    const disabled = immStepsState?.get('readOnlyForms') && stepFinished;
    const ibanError = immIban?.get('error');
    const userIsCreditCard = userProfile.investmentType === CREDIT_CARD;

    return (
        <section className="iban-validation">
            <div className="iban-validation__info">
                <p className="iban-validation__info__title">
                    <FormattedText value="identification-makeATransefer" />
                </p>
                <p>
                    <FormattedText value="identification-simbolicTransfer" />
                </p>
                <p>
                    <FormattedText value="identification-transferMethodDuration" />
                </p>
            </div>
            <div className="iban-validation__advise">
                {!isMobile() && (
                    <MessageBox
                        boxColor="blue"
                        iconColor="info-blue"
                        titleText={formatText('identification-keepInMind')}
                        contentText={formatText('identification-youWillContinueOnMobile')}
                    />
                )}
            </div>
            <div className="iban-validation__validate">
                <p className="iban-validation__validate__title">
                    <FormattedText value="identification-whatDoYouNeed" />
                </p>

                <ul className={'instruction-list check-list'}>
                    <li className="instruction-item">
                        <p className="instruction-title">{formatText('identification-headlineRequirment')}</p>
                        <p className="instruction-text">{formatText('identification-ibanRequirment')}</p>
                    </li>
                </ul>
                <Form model="userProfile" className={ibanIdentity ? '' : 'form__input--error'}>
                    <OKIban
                        errorEntity="validation-ibanEntityError"
                        errorFormat="validation-ibanFormatError"
                        errorRequired="validation-ibanNull"
                        errorCountry="validation-ibanCountryValidation"
                        validCountries={[COUNTRY_CODE_ES]}
                        notAllowedEntities={[
                            { code: OPENBANK_CODE, errorMessage: 'validation-preValidationIbanOpen' },
                            { code: BANKIA_CODE, errorMessage: 'validation-preValidationIbanBakia' },
                        ]}
                        placeholder={formatText('identification-ibanInputPlaceholder')}
                        disabled={disabled}
                        handleValidationError={onHandleValidationError}
                        eventRecoding={recordValidOrInvalidIban}
                        withImmediateValidation={!userIsCreditCard}
                    />
                </Form>
                <ConditionalToggling condition={showIbanDisclaimer && !ibanError}>
                    <MessageBox
                        contentText={
                            {
                                [CREDIT_CARD]: formatText('iban-domicileAndTransferOpenBank'),
                                [ROBO]: formatText('iban-domicileAndTransferRobo'),
                                [TRADITIONAL]: formatText('iban-domicileAndTransferTraditional'),
                            }[userProfile.investmentType]
                        }
                        boxColor={'yellow'}
                        iconColor={'warning-2-center'}
                    />
                </ConditionalToggling>

                <FeatureTogglingRender notAllowedProductKey={ONLINE_IBAN}>
                    <div className={`iban-legal-check`}>
                        <IbanTermsClause
                            userProfile={userProfile}
                            formActions={formActions}
                            userProfileForm={userProfileForm}
                        />
                    </div>
                </FeatureTogglingRender>
            </div>
        </section>
    );
};

IbanValidation.propTypes = {
    immStepsState: PropTypes.object,
    showTermsErrorMessage: PropTypes.bool,
    immIban: PropTypes.object,
    formActions: PropTypes.object,
    userProfile: PropTypes.object,
    userProfileForm: PropTypes.object,
    ibanActions: PropTypes.object,
    preLoaderActions: PropTypes.object,
    OKIbanActions: PropTypes.object,
    handleShowIbanErrorModal: PropTypes.func,
};

export { IbanValidation };
